import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/EditRounded';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import { Flex, FlexColumn } from 'src/components/flex';
import FactRow from 'src/components/lead-sidebar/FactRow';
import LeadSidebarExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import LeadSidebarPropertyNotes from 'src/components/lead-sidebar/LeadSidebarPropertyNotes';
import CreateOrUpdatePropertyModal from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal';
import { useProperty } from 'src/services/api';
import type { IProperty } from 'src/services/api/properties/types';
import { isStringPropertyFact } from 'src/services/api/properties/property-fact-values';
import { PROPERTY_FACT_KEY_LABELS } from 'src/services/api/properties/property-fact-keys';

import styles from 'src/components/lead-sidebar/LeadSidebarPropertyTab.module.css';

function formatAddress(p: IProperty): string {
    const street = p.unit ? `${p.street_address}, Unit ${p.unit}` : p.street_address;
    return `${street}, ${p.city}, ${p.state} ${p.zipcode}`;
}

function NoKnownFacts() {
    return (
        <Typography variant="body2" color="text.secondary" className={styles.noKnownFacts}>
            no known facts
        </Typography>
    );
}

interface UnitSectionProps {
    property: IProperty;
    title: string;
}

function UnitSection({ property, title }: UnitSectionProps) {
    const [isEditing, setIsEditing] = useState(false);
    const facts = property.facts ? Object.values(property.facts).filter(isStringPropertyFact) : [];
    const editAction = (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }} data-testid="edit-unit-property-button">
            <EditIcon fontSize="small" />
        </IconButton>
    );
    return (
        <>
            <LeadSidebarExpandableItem title={title} defaultExpanded action={editAction}>
                <FlexColumn rowGap={1.5} pb={1}>
                    <Typography variant="body2" fontWeight={500} className={styles.sectionName}>
                        {property.name}
                    </Typography>
                    <FactRow label="Address" value={formatAddress(property)} factKey="address" />
                    {property.rent != null && (
                        <FactRow label="Rent" value={`$${property.rent.toLocaleString()}/mo`} factKey="rent" />
                    )}
                    {property.showing_scheduling_link && (
                        <div className={styles.factRow}>
                            <Typography variant="body2" color="text.secondary">Schedule a tour</Typography>
                            <Link href={property.showing_scheduling_link.link} target="_blank" rel="noreferrer" variant="body2">
                                <span className={styles.linkRow}>
                                    Open link <OpenInNewOutlinedIcon className={styles.externalLinkIcon} />
                                </span>
                            </Link>
                        </div>
                    )}
                    {property.application_link && (
                        <div className={styles.factRow}>
                            <Typography variant="body2" color="text.secondary">Apply</Typography>
                            <Link href={property.application_link.link} target="_blank" rel="noreferrer" variant="body2">
                                <span className={styles.linkRow}>
                                    Open link <OpenInNewOutlinedIcon className={styles.externalLinkIcon} />
                                </span>
                            </Link>
                        </div>
                    )}
                    {facts.map((fact) => (
                        <FactRow
                            key={fact.key}
                            label={PROPERTY_FACT_KEY_LABELS[fact.key] ?? fact.key}
                            value={fact.value}
                            factKey={fact.key}
                        />
                    ))}
                    {facts.length === 0 && <NoKnownFacts />}
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {isEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditing}
                    onClose={() => setIsEditing(false)}
                    property={property}
                />
            )}
        </>
    );
}

interface CommunitySectionProps {
    community: IProperty;
}

function CommunitySection({ community }: CommunitySectionProps) {
    const [isEditing, setIsEditing] = useState(false);
    const facts = community.facts ? Object.values(community.facts).filter(isStringPropertyFact) : [];
    const editAction = (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }} data-testid="edit-community-property-button">
            <EditIcon fontSize="small" />
        </IconButton>
    );
    return (
        <>
            <LeadSidebarExpandableItem title="Community Facts" defaultExpanded action={editAction}>
                <FlexColumn rowGap={1.5} pb={1}>
                    <Typography variant="body2" fontWeight={500} className={styles.sectionName}>
                        {community.name}
                    </Typography>
                    {facts.map((fact) => (
                        <FactRow
                            key={fact.key}
                            label={PROPERTY_FACT_KEY_LABELS[fact.key] ?? fact.key}
                            value={fact.value}
                            factKey={fact.key}
                        />
                    ))}
                    {facts.length === 0 && <NoKnownFacts />}
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {isEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditing}
                    onClose={() => setIsEditing(false)}
                    property={community}
                />
            )}
        </>
    );
}

type Props = {
    leadPropertyId: number | null;
};

export default function LeadSidebarPropertyTab({ leadPropertyId }: Props) {
    const { data: property, isLoading } = useProperty(leadPropertyId, {
        include_ancestors: true,
        include_facts: true,
    });

    if (!leadPropertyId) {
        return (
            <Typography variant="body2" color="text.secondary" p={2}>
                This lead is not associated with a property.
            </Typography>
        );
    }

    if (isLoading) {
        return (
            <Flex justifyContent="center" py={2}>
                <CircularProgress size={20} />
            </Flex>
        );
    }

    if (!property) { return null; }

    const unitTitle = property.parent ? 'Unit Facts' : 'Property Facts';

    return (
        <>
            <LeadSidebarPropertyNotes property={property} />
            <UnitSection property={property} title={unitTitle} />
            {property.parent && <CommunitySection community={property.parent} />}
        </>
    );
}
